(() => {
  if (window.libaseProductPurchase) { window.libaseProductPurchase.init(document); return; }
  const instances = new Map();
  const text = node => (node?.textContent || '').replace(/\s+/g, ' ').trim();
  const setText = (node, value) => { if (node && node.textContent !== String(value)) node.textContent = String(value); };
  const visible = node => Boolean(node && !node.closest('[hidden]') && node.getClientRects().length && getComputedStyle(node).visibility !== 'hidden');
  const passiveContent = '[data-mobile-purchase],[data-product-total],[data-purchase-facts],.libase-product-accordion,media-gallery,libase-arrival-estimate,libase-review-summary,.libase-product-reviews,[data-libase-reviews],[data-builder-heading-price]';
  const purchaseControls = '[data-option-id],.po-price-summary-root,.po-price-summary-price,form[data-type="add-to-cart-form"],variant-selects,quantity-input,[name="quantity"],[name="id"],input[name="properties[_po:items:addon]"],[data-bundle-step],[data-box-choice],[data-bundle-complete]';
  const informationKey = label => {
    if (/^(素材|材質|Material)/i.test(label)) return 'materials';
    if (/ケア|お手入れ|ご使用方法|使用方法|金属別/i.test(label)) return 'care';
    if (/^(サイズ|寸法|チェーン|トップサイズ|Size)/i.test(label)) return 'size';
    if (/配送|送料|発送|納期|Shipping|Delivery/i.test(label)) return 'shipping';
    if (/支払|決済|Payment/i.test(label)) return 'payment';
    if (/質問|FAQ|ご注意|注意事項|返品|キャンセル|変更|お問い合わせ/i.test(label)) return 'faq';
    return 'details';
  };

  class ProductPurchase {
    constructor(root) {
      this.root = root;
      this.abort = new AbortController();
      try { this.config = JSON.parse(root.querySelector('[data-purchase-config]')?.textContent || '{}'); }
      catch (_) { this.config = {}; }
      this.labels = this.config.labels || {};
      this.bundle = Boolean(this.config.bundle);
      this.bar = root.querySelector('[data-mobile-purchase]');
      this.mobileButton = this.bar?.querySelector('[data-mobile-add]');
      this.mobileTotal = this.bar?.querySelector('[data-mobile-total]');
      this.total = root.querySelector('[data-purchase-total]');
      this.facts = root.querySelector('[data-purchase-facts]');
      this.timers = new Set();
      this.variants = new Map((this.config.variants || []).map(variant => [String(variant.id), variant]));
      this.prepareFacts();
      this.prepareInformation();
      this.listen(root, 'input', event => { if (!event.target.closest(passiveContent)) this.schedule(); });
      this.listen(root, 'change', event => { if (!event.target.closest(passiveContent)) this.schedule(); });
      this.listen(root, 'click', event => { if (event.target.closest('[data-option-id],[data-bundle-step],[data-box-choice]')) this.schedule(); });
      this.listen(document, 'easify:options-change', () => this.schedule());
      this.listen(root, 'libase:bundle-state', event => { this.bundleState = event.detail; this.refresh(); });
      this.listen(root, 'focusin', () => this.keyboard());
      this.listen(root, 'focusout', () => this.later(() => this.keyboard(), 100));
      this.listen(window, 'resize', () => this.keyboard());
      if (window.visualViewport) this.listen(window.visualViewport, 'resize', () => this.keyboard());
      this.listen(this.mobileButton, 'click', () => this.activate());
      this.observer = new MutationObserver(records => {
        if (records.some(record => this.purchaseMutation(record))) this.schedule();
      });
      this.observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['value','checked','disabled','aria-disabled','aria-pressed','aria-selected','aria-invalid','class'] });
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, event => {
          if (String(event.data?.sectionId) !== String(this.config.sectionId)) return;
          const variant = event.data?.variant;
          if (variant?.id) this.variants.set(String(variant.id), { ...this.variants.get(String(variant.id)), ...variant });
          this.schedule();
        });
      }
      this.refresh();
      this.later(() => this.refresh(), 500);
      this.later(() => this.refresh(), 1500);
    }

    listen(target, name, callback) { target?.addEventListener(name, callback, { signal: this.abort.signal }); }
    purchaseMutation(record) {
      const target = record.target.nodeType === Node.ELEMENT_NODE ? record.target : record.target.parentElement;
      if (!target || target.closest(passiveContent)) return false;
      if (target.closest(purchaseControls)) return true;
      if (record.type === 'childList') {
        return [...record.addedNodes, ...record.removedNodes].some(node => node.nodeType === Node.ELEMENT_NODE && (node.matches(purchaseControls) || node.querySelector(purchaseControls)));
      }
      return target !== this.root && /\b(?:tpo|po)[-_]/.test(String(target.className || '')) && Boolean(target.querySelector(purchaseControls));
    }
    later(callback, delay) {
      const timer = setTimeout(() => { this.timers.delete(timer); if (this.root.isConnected) callback(); }, delay);
      this.timers.add(timer);
      return timer;
    }
    schedule() {
      clearTimeout(this.refreshTimer);
      clearTimeout(this.settleTimer);
      this.settlingUntil = Date.now() + 400;
      this.refreshTimer = this.later(() => this.refresh(), 0);
      this.settleTimer = this.later(() => this.refresh(), 450);
    }
    money(minor) {
      try { return new Intl.NumberFormat(this.config.locale || 'ja-JP', { style: 'currency', currency: this.config.currency || 'JPY' }).format(minor / 100); }
      catch (_) { return `¥${Math.round(minor / 100).toLocaleString('ja-JP')}`; }
    }

    prepareInformation() {
      const accordion = this.root.querySelector('[data-product-accordion]');
      if (!accordion || accordion.dataset.prepared === 'true') return;
      accordion.dataset.prepared = 'true';
      const bodies = new Map([...accordion.querySelectorAll('[data-information-key]')].map(row => [row.dataset.informationKey, row.querySelector('[data-information-body]')]));
      const legacy = accordion.querySelector('[data-legacy-description]');
      if (legacy) {
        let key = 'details';
        for (const node of [...legacy.childNodes]) {
          if (node.nodeType === Node.ELEMENT_NODE && /^H[2-6]$/.test(node.tagName)) key = informationKey(text(node));
          if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'TABLE') {
            for (const row of [...node.querySelectorAll('tr')]) {
              const destination = informationKey(text(row.cells?.[0]));
              if (!['materials','size','care'].includes(destination)) continue;
              const table = node.cloneNode(false);
              table.removeAttribute('id');
              const tbody = document.createElement('tbody');
              if (node.caption) table.append(node.caption);
              table.append(tbody); tbody.append(row); bodies.get(destination)?.append(table);
            }
            if (!node.querySelector('tr')) { node.remove(); continue; }
          }
          const firstStrong = node.nodeType === Node.ELEMENT_NODE ? node.querySelector('strong') : null;
          const specific = firstStrong && informationKey(text(firstStrong));
          const destination = specific && specific !== 'details' && node.tagName === 'P' ? specific : key;
          bodies.get(destination)?.append(node);
        }
        legacy.remove();
      }
      for (const [key, body] of bodies) {
        const substantive = [...body.childNodes].some(node => node.nodeType === Node.TEXT_NODE ? Boolean(text(node)) : node.nodeType === Node.ELEMENT_NODE && !/^(H[1-6]|SCRIPT|STYLE|TEMPLATE)$/.test(node.tagName) && Boolean(text(node) || node.matches('img,video,iframe') || node.querySelector('img,video,iframe')));
        body.closest('details').hidden = !substantive;
        const details = body.closest('details');
        const summary = details.querySelector('summary');
        summary.setAttribute('aria-expanded', String(details.open));
        this.listen(details, 'toggle', () => summary.setAttribute('aria-expanded', String(details.open)));
        if (!substantive && key !== 'details') details.open = false;
      }
    }

    prepareFacts() {
      if (!this.facts || this.facts.dataset.prepared === 'true') return;
      this.facts.dataset.prepared = 'true';
      const source = this.root.querySelector('[data-legacy-description]');
      const list = this.facts.querySelector('[data-fact-list]');
      const explicitIncluded = Boolean(this.facts.querySelector('[data-included-fact]'));
      const append = (value, marker) => {
        if (!value || this.facts.querySelector(`[${marker}]`)) return;
        const li = document.createElement('li'); li.setAttribute(marker, ''); li.textContent = value; list.append(li);
      };
      if (source) {
        const paragraphs = [...source.querySelectorAll('p,li,tr')].map(text).filter(Boolean);
        const set = paragraphs.find(value => /(?:[2-9２-９]\s*[点本個]|単品|セット内容)/.test(value) && value.length < 140);
        append(set, 'data-set-fact');
        const included = paragraphs.find(value => /無料.*付属|標準.*付属|ギフトボックス付き/.test(value) && value.length < 180);
        append(included, 'data-included-fact');
        const paid = paragraphs.find(value => /追加.*(?:箱|ギフトボックス|ショッパー)|(?:箱|ギフトボックス).*追加/.test(value) && value.length < 180);
        append(paid, 'data-paid-fact');
        const important = paragraphs.find(value => /返品|キャンセル/.test(value) && /お受け|不可|できません/.test(value));
        const conditions = important?.match(/[^。]+。|[^。]+$/g) || [];
        append(conditions.filter((value, index) => index === 0 || /[7７]日以内/.test(value)).join(''), 'data-condition-fact');
        const boxAlert = paragraphs.find(value => /ギフトボックス.*(?:品切れ|代用|変更)/.test(value));
        if (!explicitIncluded) append(boxAlert, 'data-packaging-alert');
      }
      const policy = this.facts.querySelector('[data-refund-source]')?.content;
      if (policy && !this.facts.querySelector('[data-condition-fact]')) {
        const condition = [...policy.querySelectorAll('p,li')].map(text).find(value => /お客様都合|お客さま都合/.test(value) && /返品|キャンセル/.test(value));
        append(condition, 'data-condition-fact');
      }
      if (!list.children.length) list.hidden = true;
    }

    arrangeOptions() {
      const container = this.bundle ? this.root.querySelector('.lbb-panel') : this.root.querySelector('.product__info-container');
      if (!container) return;
      const fields = [...container.querySelectorAll('[data-option-id]')].filter(field => !field.parentElement.closest('[data-option-id]'));
      fields.forEach(field => {
        const label = text(field.querySelector('label,[class*="label-text"],[class*="labelText"],[class*="label"]'));
        const extra = /キャンドル|キーリング|キーホルダー|candle|key\s*ring/i.test(label);
        const role = extra ? 'extra' : 'main';
        if (field.dataset.libaseOptionRole !== role) field.dataset.libaseOptionRole = role;
        let parent = field.parentElement;
        while (parent && parent !== container) {
          if (!parent.hasAttribute('data-libase-flat')) parent.setAttribute('data-libase-flat', '');
          for (const sibling of parent.children) {
            if (sibling === field || sibling.hasAttribute('data-libase-flat') || sibling.hasAttribute('data-option-id') || /^(SCRIPT|STYLE|INPUT|TEMPLATE)$/.test(sibling.tagName)) continue;
            if (!sibling.querySelector('[data-option-id]') && !sibling.hasAttribute('data-libase-option-role')) sibling.dataset.libaseOptionRole = 'helper';
          }
          parent = parent.parentElement;
        }
      });
    }

    nativeControls() {
      this.form = document.getElementById(this.config.formId);
      this.submit = this.bundle ? this.root.querySelector('[data-bundle-complete]') : this.root.querySelector('[id^="ProductSubmitButton-"]');
      this.builder = this.bundle ? window.__libaseBundleLayoutFix?.instances?.get(this.root) : null;
    }

    firstMissing() {
      if (this.builder) {
        for (let slot = 0; slot < this.builder.selection.length; slot++) {
          const choice = this.builder.selection[slot];
          if (!choice?.variant) return this.builder.stepEls[slot]?.querySelector('button,select');
          if (!this.builder.shouldShowEngraving(choice.product, choice.variant)) continue;
          const state = this.builder.personalizationState(slot, choice.product.key);
          if (choice.product.engravingRequired && !String(state.text || '').trim()) return this.builder.stepEls[slot]?.querySelector('[data-engraving]');
          if (String(state.text || '').length > this.builder.engravingMax(choice.product, choice.variant)) return this.builder.stepEls[slot]?.querySelector('[data-engraving]');
        }
      }
      for (const input of this.root.querySelectorAll('input,select,textarea')) {
        if (!visible(input) || input.disabled || input.type === 'hidden' || input.closest('[data-product-accordion],libase-arrival-estimate')) continue;
        if ((input.validity && !input.validity.valid) || input.getAttribute('aria-invalid') === 'true' || (input.getAttribute('aria-required') === 'true' && !String(input.value || '').trim())) return input;
      }
      for (const field of this.root.querySelectorAll('[data-option-id]')) {
        if (!visible(field)) continue;
        const label = field.querySelector('label,[class*="label"]');
        if (!/[*＊]/.test(text(label)) && field.getAttribute('aria-required') !== 'true') continue;
        const inputs = [...field.querySelectorAll('input:not([type="hidden"]),textarea,select')].filter(input => !input.disabled && visible(input));
        if (inputs.length && !inputs.some(input => /^(radio|checkbox)$/.test(input.type) ? input.checked : String(input.value || '').trim())) return inputs[0];
      }
      return null;
    }

    paidAmount(quantity) {
      const nodes = [...this.root.querySelectorAll('input[name="properties[_po:items:addon]"]')].filter(node => !node.form || node.form === this.form);
      const payloads = [...new Set(nodes.map(node => node.value).filter(Boolean))];
      if (payloads.length > 1) return null;
      if (!payloads.length) {
        const price = this.root.querySelector('.po-price-summary-price');
        if (price && visible(price) && /[1-9]/.test(text(price))) return null;
        return 0;
      }
      try {
        const payload = JSON.parse(payloads[0]);
        if (!Array.isArray(payload)) return null;
        let total = 0;
        for (const fee of payload.flat()) {
          const price = Number(fee.price), qty = Number(fee.variantQty || 1);
          if (!Number.isFinite(price) || price < 0 || !Number.isInteger(qty) || qty < 1 || fee.oneTimeCharge?.enable) return null;
          total += Math.round(price * 100) * qty * quantity;
        }
        return total;
      } catch (_) { return null; }
    }

    delivery(states) {
      const node = this.facts?.querySelector('[data-purchase-delivery]');
      const exceptional = states.filter(state => state && state.status !== 'ready' && state.status !== 'not_shipping');
      const reservation = exceptional.some(state => ['backorder','manual'].includes(state.status));
      const notes = [...new Set(exceptional.map(state => state.note).filter(Boolean))];
      let value = notes.join(' / ');
      if (!value && reservation) value = this.labels.preorder;
      if (!value && exceptional.some(state => state.status === 'unknown_stock')) value = this.labels.deliveryUnknown;
      if (!value && !this.bundle) value = this.config.shippingNote || '';
      if (!value && this.bundle) value = this.config.shippingNote || '';
      setText(node, value || '');
      if (node) { node.hidden = !value; node.dataset.reservation = String(reservation); }
      const heading = this.root.querySelector('[data-heading-delivery]');
      if (heading) heading.hidden = true;
    }

    refresh() {
      this.nativeControls();
      this.arrangeOptions();
      if (!this.submit || !this.mobileButton) return;
      const missing = this.firstMissing();
      let available, amount, pending = false, busy;
      if (this.bundle) {
        if (!this.builder) { pending = true; available = true; }
        else {
          available = Boolean(this.builder.boxChoice?.available && this.builder.selection.every(choice => choice?.variant?.available));
          amount = this.bundleState?.total;
          pending = Boolean(this.bundleState?.errors?.length) || !Number.isFinite(amount);
          busy = this.builder.isSubmitting;
          this.delivery(this.builder.selection.map(choice => choice?.variant?.delivery).filter(Boolean));
        }
      } else {
        const variantId = this.form?.querySelector('[name="id"]')?.value;
        const variant = this.variants.get(String(variantId));
        available = Boolean(variant?.available);
        const quantityInput = this.root.querySelector('[name="quantity"]');
        const quantity = Math.max(1, Number(quantityInput?.value) || 1);
        const unit = (variant?.breaks || []).filter(price => quantity >= price.min).sort((a,b) => b.min-a.min)[0]?.price ?? variant?.price;
        const extra = this.paidAmount(quantity);
        const fields = [...this.root.querySelectorAll('[data-option-id]')].some(visible);
        pending = extra === null || (fields && Date.now() < (this.settlingUntil || 0));
        if (Number.isFinite(unit) && extra !== null && (!quantityInput || quantityInput.validity.valid)) amount = unit * quantity + extra;
        busy = this.submit.getAttribute('aria-disabled') === 'true' || this.submit.classList.contains('loading');
        pending ||= Boolean(variant?.available && this.submit.disabled && !busy);
        this.delivery(variant?.delivery ? [variant.delivery] : []);
      }
      busy ||= Date.now() < (this.proxyUntil || 0);
      const money = Number.isFinite(amount) && !pending ? this.money(amount) : '—';
      setText(this.total, money); setText(this.mobileTotal, money);
      setText(this.root.querySelector('[data-builder-heading-price]'), money);
      const label = !available ? this.labels.soldOut : busy ? this.labels.adding : missing ? this.labels.choose : this.labels.add;
      setText(this.mobileButton, label);
      this.mobileButton.disabled = !available || Boolean(busy) || (pending && !missing);
      this.mobileButton.setAttribute('aria-busy', String(Boolean(busy)));
      this.bar.hidden = Boolean(available && pending && !missing && !busy);
      this.keyboard();
    }

    activate() {
      this.nativeControls();
      if (!this.submit || this.mobileButton.disabled) return;
      const missing = this.firstMissing();
      if (missing) {
        for (let parent = missing.parentElement; parent && parent !== this.root; parent = parent.parentElement) if (parent.tagName === 'DETAILS') parent.open = true;
        missing.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        missing.focus({ preventScroll: true });
        return;
      }
      this.proxyUntil = Date.now() + 650;
      this.submit.click();
      this.refresh();
      this.later(() => this.refresh(), 700);
    }

    keyboard() {
      const active = document.activeElement;
      const editing = this.root.contains(active) && active.matches('textarea,input:not([type="radio"]):not([type="checkbox"]):not([type="hidden"])');
      this.root.classList.toggle('libase-keyboard-open', Boolean(editing && matchMedia('(max-width: 749px)').matches));
    }

    destroy() {
      this.abort.abort(); this.observer.disconnect(); this.unsubscribe?.();
      this.timers.forEach(timer => clearTimeout(timer));
      const accordion = this.root.querySelector('[data-product-accordion]');
      if (accordion) delete accordion.dataset.prepared;
      this.root.querySelectorAll('[data-libase-flat],[data-libase-option-role]').forEach(node => { node.removeAttribute('data-libase-flat'); node.removeAttribute('data-libase-option-role'); });
    }
  }

  const rootsIn = scope => [...(scope.matches?.('[data-libase-purchase]') ? [scope] : []), ...(scope.querySelectorAll?.('[data-libase-purchase]') || [])];
  const init = scope => {
    for (const [root, instance] of instances) if (!root.isConnected) { instance.destroy(); instances.delete(root); }
    rootsIn(scope).forEach(root => { if (!instances.has(root)) instances.set(root, new ProductPurchase(root)); });
  };
  window.libaseProductPurchase = { init, instances };
  init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => rootsIn(event.target).forEach(root => { instances.get(root)?.destroy(); instances.delete(root); }));
})();
